import { Box, Stack, Text } from '@mantine/core';
import { type ReactNode,useEffect, useRef } from 'react';

import { track } from '../../lib/telemetry';

/**
 * EmptyState — soft, forward-looking empty surface.
 *
 * Voice rules from prd/02_voice.md: no "Oops!" / "Sorry!". Title sets the
 * tone, supportive copy points at the next move. Glyph slot accepts text or
 * an SVG; default is a tonal sun-disc dot.
 *
 * Telemetry: fires `empty_state.viewed` on mount and
 * `empty_state.cta_clicked` when the action slot is clicked. The `reason`
 * dim is a short slice of the title — surface (pagePath) is carried by the
 * worker side so we don't need to send it here.
 */
export function EmptyState({
  glyph,
  title,
  body,
  action,
}: {
  glyph?: ReactNode;
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
}) {
  const reason = stringifyNode(title).slice(0, 60) || 'unknown';

  // One emit per mount. The reason is captured in a ref so we don't refire
  // if the title prop happens to change mid-life.
  const reasonRef = useRef<string>(reason);
  useEffect(() => {
    track('empty_state.viewed', { reason: reasonRef.current });
  }, []);

  return (
    <Box
      py={48}
      px="md"
      style={{
        textAlign: 'center',
        background:
          'radial-gradient(circle at center, ' +
          'light-dark(var(--mantine-color-brand-0), var(--wwaave-brand-border))' +
          ' 0%, transparent 70%)',
        borderRadius: 16,
      }}
    >
      <Stack gap="md" align="center">
        <Box
          aria-hidden
          style={{
            width: 56,
            height: 56,
            borderRadius: 999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background:
              'light-dark(' +
              'linear-gradient(135deg, var(--mantine-color-brand-1), var(--mantine-color-sun-1)), ' +
              'linear-gradient(135deg, var(--wwaave-brand-focus-ring), color-mix(in srgb, var(--mantine-color-sun-6) 12%, transparent))' +
              ')',
            color: 'var(--wwaave-brand-primary)',
            fontSize: 24,
            fontWeight: 600,
          }}
        >
          {glyph ?? '·'}
        </Box>
        <Stack gap={4} align="center">
          <Text fw={600} size="lg">
            {title}
          </Text>
          {body ? (
            <Text c="dimmed" size="sm" style={{ maxWidth: 420 }}>
              {body}
            </Text>
          ) : null}
        </Stack>
        {action ? (
          <Box
            onClickCapture={() => {
              track('empty_state.cta_clicked', { reason: reasonRef.current });
            }}
          >
            {action}
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
}

/**
 * Best-effort coercion of a ReactNode to a string for the telemetry `reason`
 * dim. We only need a short, low-cardinality hint of what surface the empty
 * state lives on — anything else is bonus.
 */
function stringifyNode(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(stringifyNode).join(' ');
  return '';
}
